import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useClient } from '../../../../contexts/ClientContext';
import {
  FiBarChart2 as BarChart3, FiPlay as Play, FiCalendar as Calendar, FiTarget as Target,
} from 'react-icons/fi';
import { RunEvaluationModal, BedrockQuotaBanner } from '../../components/evaluations';
import SchedulesTab from '../../../settings/components/SchedulesTab';
import JudgeRubricsTab from '../../../settings/components/JudgeRubricsTab';
import OverviewContent from './evaluation/OverviewTab';
import RunsContent from './evaluation/RunsTab';

// ============================================================================
// TAB CONFIGURATION
// ============================================================================

// Tab configuration - Compare moved to PromptDetailPage
const TABS = [
  { id: 'overview', label: 'Overview', icon: BarChart3 },
  { id: 'runs', label: 'Test Runs', icon: Play },
  { id: 'schedules', label: 'Schedules', icon: Calendar },
  { id: 'judge-rubrics', label: 'Judge Rubrics', icon: Target }
];

const EvaluationPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { selectedClientId } = useClient();
  const activeTab = searchParams.get('tab') || 'overview';
  const [showRunModal, setShowRunModal] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const handleTabChange = (tabId: string) => {
    setSearchParams({ tab: tabId });
  };

  const handleEvaluationStarted = (_result: unknown) => {
    // Switch to runs tab and refresh
    setSearchParams({ tab: 'runs' });
    setRefreshKey(prev => prev + 1);
  };

  return (
    <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8 flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white transition-colors">
            Evaluation
          </h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400 transition-colors">
            Monitor LLM evaluation results, run evaluations against datasets, and compare versions
          </p>
        </div>
        <button
          onClick={() => setShowRunModal(true)}
          className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
        >
          <Play className="w-4 h-4" />
          Run Evaluation
        </button>
      </div>

      {/* Tabs */}
      <div className="mb-6 border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex space-x-8">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors flex items-center gap-2 ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Bedrock Quota Banner - Shows when Bedrock credentials are configured */}
      <BedrockQuotaBanner
        hasActiveEvaluations={activeTab === 'runs'}
        compact={activeTab !== 'runs'}
      />

      {/* Tab Content */}
      {activeTab === 'overview' && <OverviewContent />}
      {activeTab === 'runs' && <RunsContent key={refreshKey} />}
      {activeTab === 'schedules' && (
        <SchedulesTab
          apiBaseUrl={process.env.REACT_APP_GENAIOPS_API_BASE_URL || 'http://localhost:8002/api/v1/genaiops'}
          clientId={selectedClientId ?? ''}
        />
      )}
      {activeTab === 'judge-rubrics' && <JudgeRubricsTab />}

      {/* Run Evaluation Modal */}
      <RunEvaluationModal
        isOpen={showRunModal}
        onClose={() => setShowRunModal(false)}
        onSuccess={handleEvaluationStarted}
      />
    </div>
  );
};

export default EvaluationPage;
